import { Layout, Row, Col, Card, Button, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './RevenueAnalytics.module.less';
import { CalendarOutlined, DashboardOutlined } from '@ant-design/icons';
import { useLayoutEffect, useState } from 'react';
import api, {
	ContentProviderDailyRevenueDto,
	ContentProviderRevenueSummaryDto,
	ProductStatus,
} from 'api';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import ReportValue from 'components/Analytics/ReportValue';
import ReportDateGraph from 'components/Analytics/ReportDateGraph';

type ParamsProps = {
	projectId: string;
};

export default function RevenueAnalytics() {
	const { t } = useTranslation();
	const history = useHistory();

	const { projectId } = useParams<ParamsProps>();
	const [isLoading, setIsLoading] = useState(true);
	const [projectStatus, setProjectStatus] = useState<ProductStatus>();
	const [revenueSummary, setRevenueSummary] =
		useState<ContentProviderRevenueSummaryDto>();
	const [hasRevenue, setHasRevenue] = useState(false);
	const [lastThirtyDaysChartData, setLastThirtyDaysChartData] = useState<
		ContentProviderDailyRevenueDto[]
	>([]);
	const [yearlyChartData, setYearlyChartData] = useState<
		ContentProviderDailyRevenueDto[]
	>([]);

	const monthToDateText = () => {
		return moment().format('MMM 1-MMM D');
	};

	useLayoutEffect(() => {
		const fetchProjectInfo = async () => {
			try {
				const project = await api.getProjectInfo(parseInt(projectId));
				setProjectStatus(project.status);

				const summary = await api.getContentProviderRevenueSummary(
					project.productId!!,
				);
				setRevenueSummary(summary);
				setHasRevenue((summary.allTime?.revenue || 0) > 0);

				const timeline = await api.getContentProviderRevenueTimeline(
					project.productId!!,
				);
				setLastThirtyDaysChartData(timeline.last30Days || []);
				setYearlyChartData(timeline.last12Months || []);
			} finally {
				setIsLoading(false);
			}
		};

		fetchProjectInfo();
	}, []);

	if (isLoading) {
		return (
			<Layout className={styles.contentHolder}>
				<Spin />
			</Layout>
		);
	}
	return (
		<>
			{projectStatus != ProductStatus.Released && !hasRevenue && (
				<Layout className={styles.contentHolder}>
					<h2>{t('financePage.RevenueAnalyticsTab.NotReleased.line1')}</h2>
					<h2>{t('financePage.RevenueAnalyticsTab.NoRevenue.line2')}</h2>
					<Button
						type="primary"
						data-testid="projectStepsLink"
						onClick={() => history.push('/project/' + projectId)}
					>
						{t('financePage.RevenueAnalyticsTab.GoToProject')}
					</Button>
				</Layout>
			)}
			{projectStatus == ProductStatus.Released && !hasRevenue && (
				<Layout className={styles.contentHolder}>
					<h2>{t('financePage.RevenueAnalyticsTab.NoRevenue.line1')}</h2>
					<h2>{t('financePage.RevenueAnalyticsTab.NoRevenue.line2')}</h2>
				</Layout>
			)}
			{hasRevenue && (
				<Layout>
					<Row gutter={[20, 32]}>
						<Col xs={12}>
							<Card
								className={styles.cardContainer}
								title={
									<div className={styles.cardHeader}>
										<CalendarOutlined />{' '}
										{t('financePage.RevenueAnalyticsTab.MonthToDate', {
											date: monthToDateText(),
										})}
									</div>
								}
							>
								<Row gutter={[20, 32]}>
									<Col xs={12}>
										<ReportValue
											testId="mtdCopiesSold"
											data={revenueSummary?.monthToDate?.copiesSold}
											translationKey="financePage.RevenueAnalyticsTab.CopiesSold"
										/>
									</Col>
									<Col xs={12}>
										<ReportValue
											testId="mtdEarnedRevenue"
											data={revenueSummary?.monthToDate?.revenue?.toLocaleString(
												'en-US',
												{
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												},
											)}
											translationKey="financePage.RevenueAnalyticsTab.EarnedRevenue"
											dataType="currency"
										/>
									</Col>
								</Row>
							</Card>
						</Col>
						<Col xs={12}>
							<Card
								className={styles.cardContainer}
								title={
									<div className={styles.cardHeader}>
										<DashboardOutlined /> {t('analyticsPage.allTime')}
									</div>
								}
							>
								<Row gutter={[20, 32]}>
									<Col xs={12}>
										<ReportValue
											testId="copiesSold"
											data={revenueSummary?.allTime?.copiesSold}
											translationKey="financePage.RevenueAnalyticsTab.CopiesSold"
										/>
									</Col>
									<Col xs={12}>
										<ReportValue
											testId="earnedRevenue"
											data={revenueSummary?.allTime?.revenue?.toLocaleString(
												'en-US',
												{
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												},
											)}
											translationKey="financePage.RevenueAnalyticsTab.EarnedRevenue"
											dataType="currency"
										/>
									</Col>
								</Row>
							</Card>
						</Col>
					</Row>
					<br />
					<Row gutter={[20, 32]}>
						<Col xs={24}>
							<Card
								className={styles.cardContainer}
								title={
									<div className={styles.cardHeader}>
										{t('financePage.RevenueAnalyticsTab.Last30DaysRevenue')}
									</div>
								}
							>
								<ReportDateGraph
									data={lastThirtyDaysChartData}
									barDataKey={[
										{
											key: 'revenue',
											fillColor: '#75C531',
											name: t('financePage.RevenueAnalyticsTab.Revenue'),
										},
									]}
									dateSpan="daily"
									name={t('financePage.RevenueAnalyticsTab.Revenue')}
									axes={[
										{
											labelTag: t('financePage.RevenueAnalyticsTab.CopiesSold'),
											data: 'copiesSold',
											offset: 10,
										},
									]}
									xLabelWidth={50}
									dataType="currency"
								/>
							</Card>
						</Col>
					</Row>
					<br />
					<Row gutter={[20, 32]}>
						<Col xs={24}>
							<Card
								className={styles.cardContainer}
								title={
									<div className={styles.cardHeader}>
										{t('financePage.RevenueAnalyticsTab.Last12MonthsRevenue')}
									</div>
								}
							>
								<ReportDateGraph
									data={yearlyChartData}
									barDataKey={[
										{
											key: 'revenue',
											fillColor: '#75C531',
											name: t('financePage.RevenueAnalyticsTab.Revenue'),
										},
									]}
									dateSpan="monthly"
									name={t('financePage.RevenueAnalyticsTab.Revenue')}
									axes={[
										{
											labelTag: t('financePage.RevenueAnalyticsTab.CopiesSold'),
											data: 'copiesSold',
											offset: 10,
										},
									]}
									xLabelWidth={50}
									dataType="currency"
								/>
							</Card>
						</Col>
					</Row>
				</Layout>
			)}
		</>
	);
}
